import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';

import {
  useGetAffiliateSlabsQuery,
  useGetAffiliateSlabHistoryQuery,
  useUpdateAffiliateSlabMutation,
} from '@/store/api/affiliateApi';
import { useState, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { imagekit } from '@/utils/constent';
import { formatReadableDateTime } from '@/utils/dateutils';
import { showToastMessage } from '@/utils';
import Pagination from '@/components/common/Pagination';
import { handleApiError } from '@/store/api/apiErrorHandler';

interface CommissionSlab {
  id: number;
  fromUser: number;
  toUser: number | null;
  percentage: number;
  createdAt?: string;
}

interface CommissionHistory {
  sno: number;
  slabId: string;
  oldRange: string;
  newRange: string;
  oldPercent: number;
  newPercent: number;
  changedBy: 'OWNER';
  date: string;
}

const AffiliateCommissionSlabs = () => {
  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{
    fromUser?: string;
    toUser?: string;
    percentage?: string;
  }>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: slabs = [], isLoading: slabsLoading } = useGetAffiliateSlabsQuery();

  const { data: historyData, isLoading: historyLoading } = useGetAffiliateSlabHistoryQuery({
    page: currentPage,
    limit: pageSize,
  });

  const [updateSlab, { isLoading: updating }] = useUpdateAffiliateSlabMutation();

  // Refocus the input after re-render
  useEffect(() => {
    if (focusedField) {
      const element = document.getElementById(focusedField);
      if (element) {
        element.focus();
      }
    }
  }, [editValues, focusedField]);

  const history =
    historyData?.data.map((item, index) => ({
      sno: index,
      slabId: item.id,
      oldRange: `${item.fromUserOld}-${item.toUserOld}`,
      newRange: `${item.fromUserNew}-${item.toUserNew}`,
      oldPercent: item.percentageOld,
      newPercent: item.percentageNew,
      changedBy: item.changedBy,
      date: formatReadableDateTime(item.changedAt),
    })) ?? [];

  const startEdit = (row: CommissionSlab) => {
    setEditRowId(row.id);
    setEditValues({
      fromUser: String(row.fromUser),
      toUser: row.toUser !== null ? String(row.toUser) : '',
      percentage: String(row.percentage),
    });
  };

  const cancelEdit = () => {
    setEditRowId(null);
    setEditValues({});
    setFocusedField(null);
  };

  const validateEdit = () => {
    if (!editValues.fromUser) {
      showToastMessage('error', 'Min user required');
      return;
    }

    if (!editValues.toUser) {
      showToastMessage('error', 'Max user required');
      return;
    }

    if (!editValues.percentage) {
      showToastMessage('error', 'Commission % is required');
      return;
    }

    const min = Number(editValues.fromUser);
    const max = Number(editValues.toUser);

    if (editValues.fromUser && editValues.toUser && !isNaN(min) && !isNaN(max) && min >= max) {
      showToastMessage('error', 'Max users must be greater than Min');
      return;
    }

    return true;
  };

  const handleSave = async (id: number) => {
    if (!validateEdit()) return;

    try {
      await updateSlab({
        id: id,
        fromUser: Number(editValues.fromUser),
        toUser: Number(editValues.toUser),
        percentage: Number(editValues.percentage),
      }).unwrap();
    } catch (err) {
      handleApiError(err, 'Something went wrong while updating slab');
    }
    cancelEdit();
  };

  const slabColumns: ColumnDef<CommissionSlab>[] = useMemo(
    () => [
      {
        header: 'S. No.',
        cell: ({ row }) => row.index + 1,
      },

      {
        header: 'Min Active Users',
        cell: ({ row }) =>
          editRowId === row.original.id ? (
            <input
              id={`fromUser-${row.original.id}`}
              type="text"
              inputMode="numeric"
              className="w-[70px] px-[6px] py-[4px] border rounded bg-success/15 text-center border border-border"
              value={editValues.fromUser ?? ''}
              onFocus={() => setFocusedField(`fromUser-${row.original.id}`)}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d*$/.test(v)) {
                  setEditValues({ ...editValues, fromUser: v });
                }
              }}
            />
          ) : (
            row.original.fromUser
          ),
      },

      {
        header: 'Max Active Users',
        cell: ({ row }) =>
          editRowId === row.original.id ? (
            <input
              id={`toUser-${row.original.id}`}
              type="text"
              inputMode="numeric"
              className="w-[70px] px-[6px] py-[4px] border rounded bg-success/15 text-center border border-border"
              value={editValues.toUser ?? ''}
              onFocus={() => setFocusedField(`toUser-${row.original.id}`)}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d*$/.test(v)) {
                  setEditValues({ ...editValues, toUser: v });
                }
              }}
            />
          ) : (
            (row.original.toUser ?? '∞')
          ),
      },

      {
        header: 'Commission %',
        cell: ({ row }) =>
          editRowId === row.original.id ? (
            <input
              id={`percentage-${row.original.id}`}
              type="text"
              inputMode="decimal"
              className="w-[70px] px-[6px] py-[4px] border rounded bg-success/15 text-center border border-border"
              value={editValues.percentage ?? ''}
              onFocus={() => setFocusedField(`percentage-${row.original.id}`)}
              onChange={(e) => {
                const v = e.target.value;
                if (/^\d*\.?\d*$/.test(v)) {
                  setEditValues({ ...editValues, percentage: v });
                }
              }}
            />
          ) : (
            `${row.original.percentage}%`
          ),
      },

      {
        header: 'Created On',
        cell: ({ row }) => moment(row.original.createdAt).format('DD-MM-YYYY'),
      },

      {
        header: 'Actions',
        cell: ({ row }) =>
          editRowId === row.original.id ? (
            <div className="flex gap-2">
              <button
                onClick={() => handleSave(row.original.id)}
                disabled={updating}
                className="text-green"
              >
                <img src={`${imagekit}/affiliate/save.svg`} alt={'save'} />
              </button>

              <button onClick={cancelEdit} className="text-danger">
                <X size={20} />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <img
                className="cursor-pointer"
                onClick={() => startEdit(row.original)}
                src={`${imagekit}/affiliate/edit.svg`}
                alt="edit"
              />
            </div>
          ),
      },
    ],
    [editRowId, editValues, updating]
  );

  const historyColumns: ColumnDef<CommissionHistory>[] = useMemo(
    () => [
      { accessorKey: 'sno', header: 'S. No.', cell: ({ row }) => row.index + 1 },
      { accessorKey: 'slabId', header: 'Slab ID' },
      { accessorKey: 'oldRange', header: 'Old Range' },
      { accessorKey: 'newRange', header: 'New Range' },
      {
        accessorKey: 'oldPercent',
        header: 'Old %',
        cell: ({ row }) => <span className="text-danger">{row.original.oldPercent}%</span>,
      },
      {
        accessorKey: 'newPercent',
        header: 'New %',
        cell: ({ row }) => <span className="text-green">{row.original.newPercent}%</span>,
      },
      { accessorKey: 'changedBy', header: 'Changed By' },
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ row }) => moment(row.original.date).format('DD-MM-YYYY'),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-7 border shadow-[0_6px_12px_-6px_rgba(0,0,0,0.50)]">
        <h2 className="font-semibold text-lg mb-4">
          Manage dynamic commission percentage based on active users
        </h2>

        <DataTable<CommissionSlab>
          data={slabs}
          columns={slabColumns}
          variant="secondary"
          isLoading={slabsLoading}
        />
      </div>

      <div className="bg-white rounded-xl p-7 border shadow-[0_6px_12px_-6px_rgba(0,0,0,0.50)]">
        <h2 className="font-semibold text-lg mb-4">Slab Change History</h2>

        <DataTable<CommissionHistory>
          data={history}
          columns={historyColumns}
          variant="secondary"
          isLoading={historyLoading}
        />
        <div className="py-8">
          <Pagination
            currentPage={currentPage}
            totalItems={historyData?.pagination?.totalItems || 0}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </div>
  );
};

export default AffiliateCommissionSlabs;
